// TODO: export dataAssembler to a CustomerPortalApiClient which handles communication with the API

import { EventStates, OrderEventTypes, FieldOrderTypes, WorkOrder, QualityNotificationPositionStatuses, QualityNotificationPositionStatus, Payment, HttpForbiddenError } from "./types"
import contentDisposition from "content-disposition"
import axios from "axios"

const apiClient = {
  
  /**
   * Intialises the API.
   * @param {string} berearToken The token to access the API.
   * @returns {Promise<boolean>} A boolean indicating success or not.
   */
  loadInit: async function(berearToken) {
    var success = false;
    var loadingError = null;

    await axios.request({
      method: "get",
      url: "/api/v1/Init",
      headers: { Authorization: "Bearer " + berearToken }
    }).then(() => {
      success = true;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error whilst initialising API.`);
    }

    return success;
  },

  /**
   * Loads the profiles (customers) for the specified API key.
   * @param {string} berearToken The token to access the API.
   * @returns {Promise<[Customer]>} An array of customers.
   */
  loadProfiles: async function (berearToken) {
    var profiles = [];
    var loadingError = null;

    await axios.request({
      method: "get",
      url: "/api/v1/Profiles",
      headers: { Authorization: "Bearer " + berearToken }
    }).then((response) => {
      profiles = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading profiles.`);
    }

    return profiles;
  },

    /**
   * Loads the profile (customer) for the given customer number.
   * @param {string} berearToken The token to access the API.
   * @returns {Promise<Customer>} A customer.
   */
  loadProfile: async function (berearToken, customerNumber) {
    var profile = [];
    var loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/Profiles/${customerNumber}`,
      headers: { Authorization: `Bearer ${berearToken}` }
    }).then((response) => {
      profile = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading profile for customer (CustomerNumber = ${customerNumber}).`);
    }

    return profile;
  },

  /**
   * Loads the balance for the given cutomer.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number to query.
   * @returns {Promise<number>} The balance.
   */
  loadProfileBalance: async function (bearerToken, customerNumber) {
    let balance = 0;
    let loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/Profiles/${customerNumber}/Balance`,
      headers: { Authorization: `Bearer ${bearerToken}` }
    }).then((response) => {
      balance = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading balance for customer (CustomerNumber = ${customerNumber}).`);
    }

    return balance;
  },

  loadCustomer: async function (bearerToken, customerNumber, customerId) {
    let customer = [];

    await axios.request({ 
      method: 'get',
      url: `/api/v1/Customers/${customerId}`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      },
    }).then((response) => {
      customer = response.data;
    }).catch((error) => {
      console.error("Error while loading Customer (Id = %d)!", customerId, error);
    });
    return customer;
  },

  /**
   * Loads the payments for the given cutomer.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number to query.
   * @returns {Promise<[Payment]>} An array of payments.
   */
  loadPayments: async function (bearerToken, customerNumber) {
    let payments = [];
    let loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/Payments?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + bearerToken }
    }).then((response) => {
      payments = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading payments for customer (CustomerNumber = ${customerNumber}).`);
    }

    return payments;
  },

  /**
   * Loads the master orders for the given customer.
   * @param {string} berearToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @returns {Promise<[MasterOrder]>} An array of master orders.
   */
  loadMasterOrders: async function (berearToken, customerNumber) {
    let masterOrders = [];
    let loadingError = null;

    await axios.request({
      method: "get",
      url: "/api/v1/MasterOrders?customerNumber=" + customerNumber,
      headers: { Authorization: "Bearer " + berearToken },
    }).then((response) => {
      masterOrders = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading master orders for customer (CustomerNumber = ${customerNumber}).`)
    }

    return masterOrders;
  },

  /**
   * Loads the master order for the customer.
   * @param {string} berearToken The token to access the API.
   * @param {string} customerNumber The customer Number
   * @param {number} masterOrderId The ID of the master order.
   * @returns {Promise<MasterOrder>} A master order.
   */
  loadMasterOrder: async function(berearToken, customerNumber, masterOrderId) {
    let masterOrder = {};
    let loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/MasterOrders/${masterOrderId}?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + berearToken }
    }).then((response) => {
      masterOrder = response.data;
    }).catch((error) => {
      // HTTP error
      if (error.response != null) {
        if (error.response.status == 403) {
          loadingError = new HttpForbiddenError(
            "You are not allowed to access this resource."
          );
          return;
        }
      }
      loadingError = new Error(
        `Error loading master orders for customer (CustomerNumber = ${customerNumber}).`
      );
    });
    
    if (loadingError != null) {
      throw loadingError;
    }

    return masterOrder;
  },

  /**
   * Loads hte order states for the whole customer.
   * @param {string} berearToken The token to access the API.
   * @returns {Promise<[object]>} An array of order states.
   */
  loadOrderStates: async function(berearToken) {
    let orderStates = [];
    let loadingError = null;

    await axios.request({
      method: "get",
      url: "/api/v1/OrderStates",
      headers: { Authorization: "Bearer " + berearToken },
    }).then((response) => {
      orderStates = response.data;
    }).catch(() => {
      loadingError = new Error(`Error whilst loading order states.`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return orderStates;
  },

  /**
   * Loads the work order for the customer.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} workOrderId The ID of the work order.
   * @returns {Promise<WorkOrder>} A work order.
   */
  loadWorkOrder: async function (bearerToken, customerNumber, workOrderId) {
    let workOrder = {};
    let loadingError = null;

    await axios.request({
      method: 'get',
      url: `/api/v1/WorkOrders/${workOrderId}`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      }
    }).then((response) => {
      workOrder = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading work order (Id = ${workOrder}) for customer (CustomerNumber = ${customerNumber}).`)
    }

    return workOrder;
  },

  loadWorkOrderV2: async function (bearerToken, customerNumber, workOrderId, { loadWorkOrderFieldOrders = true, loadFieldOrder = true } = {}) {
    let workOrder = {};
    await axios.request({
      method: 'get',
      url: `/api/v2/WorkOrders/${workOrderId}`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      }
    }).then((response) => {
      workOrder = response.data;
    }).catch((error) => {
      console.error("Error while loading v2/WorkOrder (Id = %d)!", workOrderId, error);
    });

    if (workOrder.WorkOrderFieldOrderIds != null && loadWorkOrderFieldOrders) {
      let workOrderFieldOrders = [];
      for (let fieldOrderId of workOrder.WorkOrderFieldOrderIds) {
        workOrderFieldOrders.push(
          await this.loadWorkOrderFieldOrderV2(bearerToken, customerNumber, workOrder.Id, fieldOrderId, { loadFieldOrder: loadFieldOrder })
        );
      }

      // eslint-disable-next-line require-atomic-updates
      workOrder.WorkOrderFieldOrders = workOrderFieldOrders;
    }

    return workOrder;
  },

  loadWorkOrderFieldOrderV2: async function (bearerToken, customerNumber, workOrderId, fieldOrderId, { loadFieldOrder = false } = {}) {
    let workOrderFieldOrder = {};
    await axios.request({
      method: 'get',
      url: `/api/v2/WorkOrderFieldOrders/${workOrderId}/${fieldOrderId}`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      }
    }).then((response) => {
      workOrderFieldOrder = response.data;
    }).catch((error) => {
      console.error("Error while loading v2/WorkOrderFieldOrder (WorkOrderId = %d, FieldOrderId = %d)!", workOrderId, fieldOrderId, error);
    });

    if (workOrderFieldOrder.FieldOrderId != null && loadFieldOrder) {
      // eslint-disable-next-line require-atomic-updates
      workOrderFieldOrder.FieldOrder =
        await this.loadFieldOrderV2(bearerToken, customerNumber, workOrderFieldOrder.FieldOrderId)
    }

    return workOrderFieldOrder;
  },

  loadFieldOrderV2: async function (bearerToken, customerNumber, fieldOrderId) {
    let fieldOrder = {};
    await axios.request({
      method: 'get',
      url: `/api/v2/FieldOrders/${fieldOrderId}`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      }
    }).then((response) => {
      fieldOrder = response.data;
    }).catch((error) => {
      console.error("Error while loading v2/FieldOrder (Id = %d)!", fieldOrderId, error);
    });
    return fieldOrder;
  },

  /**
   * Loads the given measurement order.
   * @param {string} berearToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} measurementOrderId The ID of the measurement order.
   * @returns {Promise<obeject>} A measurement order.
   */
  loadMeasurementOrder: async function(berearToken, customerNumber, measurementOrderId) {
    let measurementOrder = {};
    let loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/MeasurementOrders/${measurementOrderId}?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + berearToken }
    }).then((response) => {
      measurementOrder = response.data;
    }).catch(() => {
      loadingError = new Error(`Error whilst loading measurement order (Id = ${measurementOrderId})`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return measurementOrder;
  },

  loadQualityNotifications: async function (bearerToken, customerNumber, { loadResponsibleUser = true, loadReportedByUser = true, loadReportedByCustomer = true, loadQualityNotificationPositions = true, loadOrderEvents = true, loadQualityNotificationFlag = true, loadComplainedWorkOrder = true, loadQualityWorkOrder = true } = {}) {
    let qualityNotifications = [];

    await axios.request({
      method: "get",
      url: `/api/v1/QualityNotifications?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + bearerToken }
    }).then((response) => {
      qualityNotifications = response.data;
    }).catch((error) => {
      console.error("Error while loading QualityNotifications for Customer (CustomerNumber = %s)!", customerNumber, error);
    });

    for (let qualityNotification of qualityNotifications) {
      if (qualityNotification.ResponsibleUserId != null && loadResponsibleUser) {
        qualityNotification.ResponsibleUser =
          await this.loadUser(bearerToken, qualityNotification.ResponsibleUserId)
      } else {
        qualityNotification.ResponsibleUser = null;
      }

      if (qualityNotification.ReportedByUserId != null && loadReportedByUser) {
        qualityNotification.ReportedByUser =
          await this.loadUser(bearerToken, qualityNotification.ReportedByUserId)
      } else {
        qualityNotification.ReportedByUser = null;
      }

      if (qualityNotification.ReportedByCustomerId != null && loadReportedByCustomer) {
        qualityNotification.ReportedByCustomer =
          await this.loadCustomer(bearerToken, customerNumber, qualityNotification.ReportedByCustomerId);
      } else {
        qualityNotification.ReportedByCustomer = null;
      }

      if (loadQualityNotificationPositions) {
        let qualityNotificationPositions = [];
        for (let qualityNotificationPositionId of qualityNotification.QualityNotificationPositionIds) {
          qualityNotificationPositions.push(
            await this.loadQualityNotificationPosition(bearerToken, customerNumber, qualityNotificationPositionId, loadComplainedWorkOrder, loadQualityWorkOrder)
          );
        }
        qualityNotification.QualityNotificationPositions = qualityNotificationPositions;
      }

      if (loadOrderEvents) {
        /*qualityNotification.OrderEvents =
          this.loadOrderEvents(bearerToken, customerNumber, qualityNotification.OrderEventIds)*/
      }

      if (loadQualityNotificationFlag) {
        qualityNotification.QualityNotificationFlag =
          await this.loadQualityNotificationFlag(bearerToken, customerNumber, qualityNotification.Id)
      }
    }

    return qualityNotifications;
  },

    /**
   * Creates a new quality notification and position in one step.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {object} qualityNotificationAndPositionDto An quality notification and position.
   * @returns {Promise<boolean>} A status indicating success.
   */
  createQualityNotification: async function(bearerToken, customerNumber, qualityNotificationAndPositionDto) {
    let success = false;
    let loadingError = null;

    await axios.request({ 
      method: "post",
      url: `/api/v1/QualityNotifications`,
      params: {
        customerNumber: customerNumber
      },
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: qualityNotificationAndPositionDto
    }).then((response) => {
      if (response.status === 200) {
        success = true;
      }
    }).catch(() => {
      loadingError = new Error(`Error whilst creating new quality notification and position.`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return success;
  },

  loadQualityNotificationFlag: async function (bearerToken, customerNumber, qualityNotificationId) {
    let qualityNotificationSource = {};
    await axios.request({
      method: 'get',
      url: `/api/v1/QualityNotifications/${qualityNotificationId}/Flag`,
      params: {
        customerNumber: customerNumber
      },
      headers: {
        Authorization: `Bearer ${bearerToken}`
      }
    }).then((response) => {
      qualityNotificationSource = response.data;
    }).catch((error) => {
      console.error("Error while loading QualityNotificationFlag for QualityNotification (Id = %d)!", qualityNotificationId, error);
    });
    return qualityNotificationSource;
  },

  loadQualityNotificationPosition: async function (bearerToken, customerNumber, qualityNotificationPositionId, { loadComplainedWorkOrder = true, loadQualityWorkOrder = true } = {}) {
    let qualityNotificationPosition = {};
    await axios.request({
      method: "get",
      url: `/api/v1/QualityNotificationPositions/${qualityNotificationPositionId}?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + bearerToken }
    }).then((response) => {
      qualityNotificationPosition = response.data;
    }).catch((error) => {
      console.error("Error while loading QualityNotificationPosition (Id = %d)!", qualityNotificationPositionId, error);
    });

    if (qualityNotificationPosition.ComplainedWorkOrderId != null && loadComplainedWorkOrder) {
    // eslint-disable-next-line require-atomic-updates
    qualityNotificationPosition.ComplainedWorkOrder =
      await this.loadWorkOrderV2(bearerToken, customerNumber, qualityNotificationPosition.ComplainedWorkOrderId);
    }
    
    if (qualityNotificationPosition.QualityWorkOrderId != null && loadQualityWorkOrder) {
      // eslint-disable-next-line require-atomic-updates
      qualityNotificationPosition.QualityWorkOrder =
        await this.loadWorkOrderV2(bearerToken, customerNumber, qualityNotificationPosition.QualityWorkOrderId);
    } else {
      // eslint-disable-next-line require-atomic-updates
      qualityNotificationPosition.QualityWorkOrder = null;
    }

    return qualityNotificationPosition;
  },

  /**
   * Load the documents for the given quality notification position.
   * @param {string} berearToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} qualityNotificationPositionId The ID of the quality notification position.
   * @returns {Promise<[object]>} An Array of documents.
   */
  loadQualityNotificationPositionDocuments: async function(berearToken, customerNumber, qualityNotificationPositionId) {
    let documents = [];
    let loadingError = null;

    await axios.request({
      method: "get",
      url: `/api/v1/QualityNotificationPositions/${qualityNotificationPositionId}/Documents?customerNumber=${customerNumber}`,
      headers: { Authorization: "Bearer " + berearToken }
    }).then((response) => {
      documents = response.data;
    }).catch(() => {
      loadingError = new Error(`Error whilst loading documents for quality notification position (Id = ${qualityNotificationPositionId}).`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return documents;
  },

  loadUser: async function (bearerToken, userId) {
    let user = [];
    await axios.request({ 
      method: 'get',
      url: `/api/v1/Users/${userId}`,
      headers: {
        Authorization: `Bearer ${bearerToken}`
      },
    }).then((response) => {
      user = response.data;
    }).catch((error) => {
      console.error("Error while loading User (Id = %d)!", userId, error);
    });
    return user;
  },

  /**
   * Loads the documents for the given master order.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} masterOrderId The ID of the master order.
   * @returns {Promise<[object]>} An array of documents.
   */
  loadDocuments: async function (bearerToken, customerNumber, masterOrderId) {
    let documents = [];
    let loadingError = null;

    await axios.request({
      method: "get",
      url: "/api/v1/Documents",
      params: {
        masterOrderId: masterOrderId,
        customerNumber: customerNumber
      },
      headers: { Authorization: "Bearer " + bearerToken },
    }).then((response) => {
      documents = response.data;
    }).catch(() => {
      loadingError = new Error(`Error whilst loading documents for master order (ID = ${masterOrderId}).`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return documents;
  },

  /**
   * Load the given document to be downloaded.
   * @param {string} berearToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} masterOrderId The ID of the master order.
   * @param {string} folderId The identifier of the document folder.
   * @param {string} documentId The identifier of the document.
   * @returns {Promise<object>} An object representing a document to download.
   */
  loadDocumentsDownload: async function (berearToken, customerNumber, masterOrderId, folderId, documentId) {
    let documentToDownload = {};
    let loadingError = null;

    await axios.request({
      url: '/api/v1/Documents/Download',
      method: "get",
      params: {
          masterOrderId: masterOrderId,
          folderId: folderId,
          fileId: documentId,
          customerNumber: customerNumber
      },
      headers: { Authorization: "Bearer " + berearToken },
      responseType: 'blob'
    }).then((response) => {
      documentToDownload = {
        Base64Content: response.data,
        FileName: Buffer.from(contentDisposition.parse(response.headers["content-disposition"]).parameters.filename, 'base64')
      }
    }).catch(() => {
      loadingError = new Error(`Error whilst loading document (Id = ${documentId}) to download.`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return documentToDownload;
  },

  /**
   * Load the LISA messages for the given customer.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number to query.
   * @returns {[object]} An array of LISA messages.
   */
  loadLisaMessages: async function(bearerToken, customerNumber) {
    let lisaMessages = [];
    let loadingError = null;

    await axios.request({ 
      method: "get",
      url: "/api/v1/Messages",
      params: {
        customerNumber: customerNumber
      },
      headers: { Authorization: "Bearer " + bearerToken },
    }).then((response) => {
      lisaMessages = response.data;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error loading LISA messages for customer (CustomerNumber = ${customerNumber}).`);
    }

    return lisaMessages;
  },

  /**
   * Load the LISA message attachment.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} messageId The id of the message.
   * @param {number} attachmentId The id of the attachment.
   * @returns {Promise<object>} A LISA message attachment.
   */
  loadLisaMessageAttachment: async function(bearerToken, customerNumber, messageId, attachmentId) {
    let attachment = {};
    let loadingError;

    await axios.request({
      url: `/api/v1/Messages/${messageId}/Attachment/${attachmentId}`,
      method: "get",
      params: {
        customerNumber: this.$store.getters.customerNumber()
      },
      headers: { Authorization: "Bearer " + this.$store.getters.getToken() },
      responseType: 'blob'
    }).then((response) => {
      attachment = {
        Base64Content: response.data,
        FileName: Buffer.from(contentDisposition.parse(response.headers["content-disposition"]).parameters.filename, 'base64')
      };
    }).catch(() => {
      loadingError = new Error(`Error whilst loading attachment (Id = ${attachmentId}) for message (Id = ${messageId}).`);
    });

    if (loadingError != null) {
      throw loadingError;
    }

    return attachment;
  },

  /**
   * Created a message attached to an order event.
   * @param {string} bearerToken The token to access the API.
   * @param {number} orderEventId The ID of the order event to attach the message to.
   * @param {string} content A text.
   * @param {[object]} attachments An array of files.
   * @returns {Promise<boolean>} A boolean indicating success.
   */
  createLisaMessage: async function(bearerToken, orderEventId, content, attachments) {
    var result = false;
    var loadingError = null;
    
    let formData = new FormData();

    for (let file of attachments) {
      formData.append("AttachmentFiles", file, file.name);
    }

    formData.append('Created', new Date().toISOString());
    formData.append('OrderEventId', orderEventId);
    formData.append('MessageContent', content);
    formData.append('ExternalSender', null); // Overriden by backend

    await axios.request({
      method: "post",
      url: "/api/v1/Messages",
      headers: { Authorization: "Bearer " + bearerToken },
      data: formData
    }).then(() => {
      result = true;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error creating LISA message attachted to order event (Id = ${orderEventId}).`);
    }

    return result;
  },

  /**
   * Create a new LISA message on the given master order.
   * @param {string} bearerToken The token to access the API.
   * @param {string} customerNumber The customer number.
   * @param {number} masterOrderId The ID of the master order.
   * @param {string} text The message text.
   * @returns {Promise<boolean>} Indicating success.
   */
  createLisaMessageNew: async function(bearerToken, customerNumber, masterOrderId, text) {
    let success = false;
    let loadingError = null;

    await axios.request({ 
      method: "post",
      url: `/api/v1/Messages/New`,
      params: {
        customerNumber: customerNumber
      },
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: {
        MasterOrderId: masterOrderId,
        Text: text
      }
    }).then(() => {
      success = true;
    }).catch((error) => {
      loadingError = error;
    });

    if (loadingError != null) {
      throw new Error(`Error whilst creating new LISA message for master order (ID = ${this.masterOrderId})`);
    }

    return success;
  },

  /**
   * Gets the display name for the work order.
   * @param {WorkOrder} workOrder The work order.
   * @returns {string} The display name for the work order.
   */
  getWorkOrderDisplayName: function (workOrder) {
    let masterOrderOrderNumber = "unknown";
    let sequenceNumber = 0;
    let isComplaintOrder = false;
    let defaultSuffix = '-WA';
    let qualitySuffix = '-QA';

    if (workOrder !== undefined && workOrder !== null) {
      if (workOrder.SequenceNumber !== undefined && workOrder.SequenceNumber !== null && !isNaN(Number.parseInt(workOrder.SequenceNumber))) {
        sequenceNumber = workOrder.SequenceNumber;
      }
      if (workOrder.IsComplaintOrder !== undefined && workOrder.IsComplaintOrder !== null) {
        isComplaintOrder = workOrder.IsComplaintOrder;
      }
      if (workOrder.MasterOrder !== undefined && workOrder.MasterOrder !== null) {
        if (workOrder.MasterOrder.OrderNumber !== undefined && workOrder.MasterOrder.OrderNumber !== null) {
          masterOrderOrderNumber = workOrder.MasterOrder.OrderNumber;
        }
      }
    }

    return `${masterOrderOrderNumber}${isComplaintOrder ? qualitySuffix : defaultSuffix}${sequenceNumber.toString().padStart(2, '0')}`;
  },

  getWorkOrderFieldOrdersFromWorkOrder: function (workOrder) {
    let result = [];

    if (workOrder === undefined || workOrder === null) {
      return result;
    }

    if (workOrder.WorkOrderFieldOrders === undefined || workOrder.WorkOrderFieldOrders === null || !Array.isArray(workOrder.WorkOrderFieldOrders) || workOrder.WorkOrderFieldOrders.length === 0) {
      return result;
    }

    return workOrder.WorkOrderFieldOrders;
  },

  getInstallationFieldOrdersFromWorkOrder: function (workOrder) {
    let result = [];
    let workOrderFieldOrders = this.getWorkOrderFieldOrdersFromWorkOrder(workOrder);
    let installationWorkOrderFieldOrders = workOrderFieldOrders.filter((wofo) => [FieldOrderTypes.LM.Id, FieldOrderTypes.M.Id].includes(wofo.FieldOrder.FieldOrderTypeId));
    if (installationWorkOrderFieldOrders.length === 0) {
        return result;
    }
    installationWorkOrderFieldOrders.forEach(installationWoFo => {
        result.push(installationWoFo.FieldOrder);
    });
    return result;
  },

  getDeliveryFieldOrdersFromWorkOrder: function (workOrder) {
    let result = [];
    let workOrderFieldOrders = this.getWorkOrderFieldOrdersFromWorkOrder(workOrder);
    let deliveryWorkOrdersFieldOrders = workOrderFieldOrders.filter((wofo) => [FieldOrderTypes.LM.Id, FieldOrderTypes.LL.Id, FieldOrderTypes.LK.Id].includes(wofo.FieldOrder.FieldOrderTypeId));
    if (deliveryWorkOrdersFieldOrders.length === 0) {
        return result;
    }
    deliveryWorkOrdersFieldOrders.forEach(deliveryWofo => {
        result.push(deliveryWofo.FieldOrder);
    });
    return result;
  },

  /**
   * @param {QualityNotificationPosition} qualityNotificationPosition 
   * @returns {QualityNotificationPositionStatus} A status
   */
  getQualityNotificationPositionStatus: function (qualityNotificationPosition) {
    if (qualityNotificationPosition === undefined && qualityNotificationPosition === null) {
      return QualityNotificationPositionStatuses.New;
    }

    if (qualityNotificationPosition.OrderEvents !== undefined && qualityNotificationPosition.OrderEvents !== null && Array.isArray(qualityNotificationPosition.OrderEvents) && qualityNotificationPosition.OrderEvents.length >= 1) {
      let orderEvents = qualityNotificationPosition.OrderEvents;
      if (orderEvents.find(x => x.OrderEventTypeId === 80) !== undefined) {
        return QualityNotificationPositionStatuses.Deleted;
      }
      if (orderEvents.find(x => x.OrderEventTypeId === 81) !== undefined) {
        return QualityNotificationPositionStatuses.Completed;
      }
      if (qualityNotificationPosition.IsAccepted === undefined || qualityNotificationPosition.IsAccepted === null) {
        return QualityNotificationPositionStatuses.New;
      }
      if (qualityNotificationPosition.IsAccepted === false) {
        return QualityNotificationPositionStatuses.Declined;
      }
      if (qualityNotificationPosition.QualityWorkOrderId !== undefined && qualityNotificationPosition.QualityWorkOrderId !== null && !isNaN(Number.parseInt(qualityNotificationPosition.QualityWorkOrderId))) {
        return QualityNotificationPositionStatuses.Processing;
      }
    }

    return QualityNotificationPositionStatuses.New;
  },

  /**
   * 
   * @param {WorkOrder[]} workOrders 
   */
  filterCanceledWorkOrders(workOrders) {
    if (workOrders == null || workOrders == undefined) {
      return [];
    }

    return workOrders.filter((workOrder) => {
      return workOrder.Events.findIndex((orderEvent) => {
        return orderEvent.OrderEventTypeId == OrderEventTypes.Canceled;
      }) == -1;
    });
  },
  
  /**
   * This method calculates the proper colorings for the TimeLine based on a WorkOrderFlag from a WorkOrder
   * 
   * @param {Array} workOrderEvents - The Events bound to that WorkOrder 
   * @param {object} workOrderFlag - The WorkOrderFlag 
   * @returns {object} Object for configuration
   */
  calculateTimeLineConfig: function(workOrderFieldOrders, workOrderEvents, workOrderFlag, isInstallationOrder) {
    let result = {};

    let resultTimeLineColors = [];
    let resultTimeLinePercent = 0;
    let resultTimeLineHideInstallationOrder = !isInstallationOrder;

    let percentToAdd = 100 / (isInstallationOrder ? 6 : 5);

    // Auftragseingang
    if (workOrderEvents.find(x => x.OrderEventTypeId == OrderEventTypes.ReadyInSystem && x.EventStateId == EventStates.Completed) == undefined) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (workOrderEvents.find(x => x.OrderEventTypeId == OrderEventTypes.ReadyInSystem && x.EventStateId == EventStates.Completed) != undefined) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }

    // Aufmaß
    if (resultTimeLineColors[resultTimeLineColors.length-1] === 'grey') { resultTimeLineColors.push('grey'); }
    else if (workOrderFlag.PlanInHouseDate == null) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (workOrderFlag.PlanInHouseDate != null) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }

    // Konstruktion
    if (resultTimeLineColors[resultTimeLineColors.length-1] === 'grey') { resultTimeLineColors.push('grey'); }
    else if (workOrderFlag.DesignEngineerStartedDate != null && workOrderFlag.EngineeringCompletedDate == null) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (workOrderFlag.EngineeringCompletedDate != null) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }

    // Produktion
    if (resultTimeLineColors[resultTimeLineColors.length-1] === 'grey') { resultTimeLineColors.push('grey'); }
    else if (workOrderFlag.EngineeringCompletedDate != null && workOrderEvents.find(x => x.OrderEventTypeId == OrderEventTypes.OrderInProductionStarted && x.EventStateId == EventStates.Completed) == undefined) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (workOrderEvents.find(x => x.OrderEventTypeId == OrderEventTypes.Produced && x.EventStateId == EventStates.Completed) != undefined) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }
 
    let deliveryWofos = workOrderFieldOrders.filter((v) => [1,2,5].findIndex(x => x == v.FieldOrder.FieldOrderTypeId) != -1);

    // Logistik
    if (resultTimeLineColors[resultTimeLineColors.length-1] === 'grey') { resultTimeLineColors.push('grey'); }
    else if (deliveryWofos.find(x => !x.IsOrderCompleted || x.FieldOrder.Tour.TourStateId == 20) != undefined) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (deliveryWofos.find(x => x.IsOrderCompleted || x.FieldOrder.Tour.TourStateId == 50) != undefined) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }

    let assemblyWofos = workOrderFieldOrders.filter((v) => [2,3].findIndex(x => x == v.FieldOrder.FieldOrderTypeId) != -1);

    // Montage
    if (resultTimeLineColors[resultTimeLineColors.length-1] === 'grey') { resultTimeLineColors.push('grey'); }
    else if (!isInstallationOrder) { resultTimeLineColors.push('grey'); }
    else if (assemblyWofos.find(x => x.IsOrderCompleted == false) != undefined) { resultTimeLineColors.push('orange'); resultTimeLinePercent += percentToAdd/2; }
    else if (assemblyWofos.find(x => x.IsOrderCompleted == false) == undefined) { resultTimeLineColors.push('green'); resultTimeLinePercent += percentToAdd; }
    else { resultTimeLineColors.push('grey'); }

    resultTimeLineColors.forEach((value, index, array) => {
      if (value !== "green") {
        if (index+1 < array.length) {
          if (array[index + 1] === "green") {
            if (array[index] === 'grey') { resultTimeLinePercent += percentToAdd; array[index] = "green"; }
            else if (array[index] === 'orange') { resultTimeLinePercent += percentToAdd/2; array[index] = "green"; }
          }
        }
      }
    });

    if (resultTimeLinePercent > 100) { resultTimeLinePercent = 100; }

    result.colors = resultTimeLineColors;
    result.percent = resultTimeLinePercent;
    result.hideInstallationOrder = resultTimeLineHideInstallationOrder;

    let expectedDeliveryFieldOrder = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 1 || v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 5 && v.IsOrderCompleted == false)?.FieldOrder;
    result.showExpectedDeliveryDateAsCalendarWeek = [25, 30, 40, 50].findIndex(targetTourStateId => targetTourStateId == expectedDeliveryFieldOrder?.Tour?.TourStateId) == -1;
    result.expectedDeliveryDate = expectedDeliveryFieldOrder?.PlannedDate;
    result.isExpectedDeliveryToWarehouse = expectedDeliveryFieldOrder?.FieldOrderTypeId == 5;

    let deliveredDeliveryFieldOrder = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 1 || v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 5 && v.IsOrderCompleted == true)?.FieldOrder;
    result.wasDeliveredToWarehouse = deliveredDeliveryFieldOrder?.FieldOrderTypeId == 5;

    let expectedAssemblyFieldOrder = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 3 && v.IsOrderCompleted == false)?.FieldOrder;
    result.showExpectedAssemblyDateAsCalendarWeek = [25, 30, 40, 50].findIndex(targetTourStateId => targetTourStateId == expectedAssemblyFieldOrder?.Tour?.TourStateId) == -1;
    result.expectedAssemblyDate = expectedAssemblyFieldOrder?.PlannedDate;

    result.actualDeliveryDate = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 1 || v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 5 && v.IsOrderCompleted == true)?.FieldOrder?.ReportDate;
    result.actualAssemblyDate = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 3 && v.IsOrderCompleted == true)?.FieldOrder?.ReportDate;
    result.isOrderConfirmed = workOrderEvents.find(x => x.OrderEventTypeId == OrderEventTypes.ReadyInSystem && x.EventStateId == EventStates.Completed) != undefined;
    result.isPrePaymentDone = true;
    result.isTechnicalCheckCompleted = workOrderFlag.EngineeringCompletedDate != null;
    result.isPlanReleased = workOrderFlag.EngineeringCompletedDate != null;
    result.isAssemblyReportAccepted = workOrderFieldOrders.find((v) => v.FieldOrder.FieldOrderTypeId == 2 || v.FieldOrder.FieldOrderTypeId == 3 && v.IsOrderCompleted == true)?.CustomerAcceptance;

    return result;
  },

  getDeliveryAddresses: function(addresses) {
    return addresses.filter((x) => x.AddressTypeId == 1);
  },

  getInvoiceAddresses: function(addresses) {
    return addresses.filter((x) => x.AddressTypeId == 2);
  },

  getFieldOrderAssemblyReport: function(masterOrderNumber, fieldOrder, documents) {
    if (fieldOrder == undefined || [2, 3].findIndex(x => x == fieldOrder.FieldOrderTypeId) == -1) {
      return [];
    }

    let fieldOrderDisplayName = new String(masterOrderNumber);
    fieldOrderDisplayName += "-";

    if (fieldOrder.FieldOrderTypeId == 2) {
      fieldOrderDisplayName += FieldOrderTypes.LM.Name;
    } else if (fieldOrder.FieldOrderTypeId == 3) {
      fieldOrderDisplayName += FieldOrderTypes.M.Name;
    }

    fieldOrderDisplayName += (fieldOrder.SequenceInMasterOrder <= 9 ? "0" : "") + fieldOrder.SequenceInMasterOrder;
    
    let result = documents.find(x => x.Name.startsWith(`MB_${fieldOrderDisplayName}`));

    if (result === undefined || result == null) {
      return [];
    } else {
      return result;
    }
  },

  getOrderConfirmations: function(masterOrderNumber, documents) {
    if (masterOrderNumber === undefined || masterOrderNumber === null || masterOrderNumber === "") {
      return [];
    }

    if (!(Array.isArray(documents))) {
      return [];
    }

    let result = documents.find(x => x.Name.startsWith(`AB_${masterOrderNumber}`));

    if (result === undefined || result == null) {
      return [];
    } else {
      return result;
    }
  },

  onceUsage() {
    return {
      payment: new Payment(),
      workOrder: new WorkOrder(),
      qualityNotificationPositionStatus: new QualityNotificationPositionStatus(null, null, null)
    };
  }
}

export default apiClient